import React, { useEffect, useState, useCallback } from 'react';
import { Container, Button, Spinner } from 'react-bootstrap';
import { FaTools, FaClock, FaExclamationTriangle, FaUserShield, FaArrowRight, FaShieldAlt, FaHome } from 'react-icons/fa';
import { useSystemStatus } from '../context/SystemStatusContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import socket from '../socket';
import '../css/MaintenanceMode.css';

// Floating particles component
const FloatingParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5,
  }));
  
  return (
    <div className="mnt-particles">
      {particles.map(p => (
        <div
          key={p.id}
          className="mnt-particle"
          style={{
            width: p.size + 'px',
            height: p.size + 'px',
            left: p.x + '%',
            top: p.y + '%',
            animationDuration: p.duration + 's',
            animationDelay: p.delay + 's',
          }}
        />
      ))}
    </div>
  );
};

// Animated gear icon
const GearAnimation = ({ isPageSpecific }) => (
  <div className="mnt-gear-wrapper">
    <div className="mnt-gear-ring mnt-gear-ring-1" />
    <div className="mnt-gear-ring mnt-gear-ring-2" />
    <div className="mnt-gear-icon-circle">
      <FaTools className="mnt-gear-icon" />
    </div>
    <div className="mnt-gear-pulse" />
  </div>
);

// Live countdown dot
const StatusDot = () => (
  <span className="mnt-status-dot">
    <span className="mnt-status-dot-ping" />
    <span className="mnt-status-dot-core" />
  </span>
);

const MaintenanceMode = ({ children, message }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    systemStatus, 
    isPageAffected, 
    getMaintenanceTimeRange, 
    isLoading, 
    bypassMaintenance,
    maintenanceBypass 
  } = useSystemStatus();
  
  const currentPath = location.pathname;
  const isSuperAdmin = user && user.role === 'SUPER_ADMIN';
  const isInWhitelist = user && user.maintenanceBypass === true;
  const isPageSpecificMaintenance = !!message;
  
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    const handlePageVisibilityChange = (data) => {
      if (data.path === currentPath && data.maintenanceMode !== undefined) {
        window.location.reload();
      }
    };
    socket.on('page_visibility_change', handlePageVisibilityChange);
    return () => { socket.off('page_visibility_change', handlePageVisibilityChange); };
  }, [currentPath]);
  
  const formatCompletionTime = useCallback(() => {
    if (!systemStatus.expectedCompletionTime) return 'Chưa xác định';
    try {
      const completionTime = new Date(systemStatus.expectedCompletionTime);
      return completionTime.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Chưa xác định';
    }
  }, [systemStatus.expectedCompletionTime]);
  
  const isCurrentPageAffected = !isPageSpecificMaintenance && isPageAffected(currentPath);
  
  if (currentPath === '/login') return children;
  if (maintenanceBypass || isInWhitelist) return children;
  if (!isPageSpecificMaintenance && (systemStatus.isActive || !isCurrentPageAffected)) return children;
  
  const timeRange = getMaintenanceTimeRange();

  // Loading state
  if (!isPageSpecificMaintenance && isLoading) {
    return (
      <div className="mnt-fullscreen">
        <FloatingParticles />
        <div className="mnt-gradient-bg" />
        <Container className="mnt-center-wrap">
          <div className={`mnt-glass-card mnt-loading-card ${mounted ? 'mnt-mounted' : ''}`}>
            <div className="mnt-loading-content">
              <div className="mnt-spinner-wrap">
                <Spinner animation="border" className="mnt-spinner" />
              </div>
              <h3 className="mnt-loading-title">Đang kiểm tra trạng thái hệ thống</h3>
              <p className="mnt-loading-subtitle">Vui lòng đợi trong giây lát...</p>
              <div className="mnt-loading-bar">
                <div className="mnt-loading-bar-fill" />
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // Main maintenance UI
  return (
    <div className="mnt-fullscreen">
      <FloatingParticles />
      <div className="mnt-gradient-bg" />
      <div className="mnt-grid-overlay" />
      
      <Container className="mnt-center-wrap">
        <div className={`mnt-glass-card mnt-main-card ${mounted ? 'mnt-mounted' : ''}`}>
          {/* Top accent bar */}
          <div className="mnt-accent-bar" />
          
          {/* Header section */}
          <div className="mnt-header">
            <GearAnimation isPageSpecific={isPageSpecificMaintenance} />
            
            <div className="mnt-badge">
              <StatusDot />
              {isPageSpecificMaintenance ? 'Trang đang bảo trì' : 'Đang bảo trì hệ thống'}
            </div>
            
            <h1 className="mnt-title">
              {isPageSpecificMaintenance 
                ? 'Trang tạm thời không khả dụng'
                : 'Hệ thống đang được nâng cấp'}
            </h1>
            
            <p className="mnt-subtitle">
              {message || systemStatus.maintenanceMessage || 'Hệ thống đang được bảo trì để nâng cấp và cải thiện dịch vụ. Vui lòng quay lại sau.'}
            </p>
          </div>
          
          {/* Info cards */}
          <div className="mnt-info-grid">
            {!isPageSpecificMaintenance ? (
              <>
                <div className="mnt-info-card mnt-info-card-time">
                  <div className="mnt-info-icon-wrap mnt-info-icon-blue">
                    <FaClock />
                  </div>
                  <div className="mnt-info-body">
                    <span className="mnt-info-label">Thời gian hoàn thành dự kiến</span>
                    <span className="mnt-info-value">{timeRange || formatCompletionTime()}</span>
                  </div>
                </div>
                
                {systemStatus.affectedModules && systemStatus.affectedModules.length > 0 && (
                  <div className="mnt-info-card mnt-info-card-modules">
                    <div className="mnt-info-icon-wrap mnt-info-icon-amber">
                      <FaExclamationTriangle />
                    </div>
                    <div className="mnt-info-body">
                      <span className="mnt-info-label">Các phân hệ bị ảnh hưởng</span>
                      <div className="mnt-module-tags">
                        {systemStatus.affectedModules.map((mod, idx) => (
                          <span key={idx} className="mnt-module-tag">{mod}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="mnt-info-card mnt-info-card-page">
                <div className="mnt-info-icon-wrap mnt-info-icon-blue">
                  <FaClock />
                </div>
                <div className="mnt-info-body">
                  <span className="mnt-info-label">Trạng thái</span>
                  <span className="mnt-info-value">Trang đang được bảo trì và sẽ sớm trở lại hoạt động</span>
                  <span className="mnt-info-hint">Bạn có thể truy cập các trang khác trong thời gian chờ đợi</span>
                </div>
              </div>
            )}
            
            <div className="mnt-home-wrap">
              <Button 
                className="mnt-home-btn"
                onClick={() => navigate('/')}
              >
                <FaHome className="mnt-home-btn-icon" />
                <span>Quay lại trang chủ</span>
              </Button>
            </div>
          </div>
          
          {/* Admin bypass section */}
          {isSuperAdmin && (
            <div className="mnt-admin-section">
              <div className="mnt-admin-badge">
                <FaShieldAlt className="mnt-admin-badge-icon" />
                <span>SUPER ADMIN</span>
              </div>
              <Button 
                className="mnt-bypass-btn"
                onClick={bypassMaintenance}
              >
                <span>Bỏ qua chế độ bảo trì</span>
                <FaArrowRight className="mnt-bypass-btn-arrow" />
              </Button>
            </div>
          )}
          
          {/* Footer */}
          <div className="mnt-footer">
            <p>Nếu cần hỗ trợ gấp, vui lòng liên hệ với quản trị viên hệ thống.</p>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default MaintenanceMode;